/// <reference path="./piwik.types.d.ts" />
import type { MatomoTrackerSpecification } from './matomo_tracker';
import { MatomoTracker } from './matomo_tracker';

export namespace Analytic {
	const trackersToBeCreated: MatomoTracker[] = [];

	let scriptElement: HTMLScriptElement | null = null;

	export function createTracker(spec: MatomoTrackerSpecification): MatomoTracker {
		// pushes all specs so they can be used when piwik gets loaded
		const matomoInstance = MatomoTracker.create(spec);
		trackersToBeCreated.push(matomoInstance);

		return matomoInstance;
	}

	export function initialize(urlBase: string): void {
		// window does not exist in non-DOM environments
		if (typeof window === 'undefined') return;

		loadScript(urlBase);
	}

	export function attachTrackers(urlBase: string): void {
		while (trackersToBeCreated.length > 0) {
			const matomoInstance = trackersToBeCreated.shift();
			if (matomoInstance == null) break;
			if (window.Piwik == null) break;

			const piwikTracker = window.Piwik.getTracker(`${urlBase}piwik.php`, matomoInstance.siteId);

			// Setting up the flag execution per instance. https://github.com/matomo-org/matomo/blob/4.x-dev/js/piwik.js#L7378
			// 🚨 Correct execution order is:
			// → disableCookies
			// → setAPIUrl
			// → enableCrossDomainLinking
			// → setCookiePath
			// → setCookieDomain
			// → setDomains
			// → setUserId
			// → enableLinkTracking
			// https://matomo.org/blog/2017/02/complete-guide-tracking-websites-web-apps-multiple-piwiks-easily-efficiently/
			// ref in source code https://github.com/matomo-org/matomo/blob/4.x-dev/js/piwik.js#L7122
			// prettier-ignore
			const fixedFnOrder = [
				matomoInstance.flags.useDisableCookies && piwikTracker.disableCookies,
				matomoInstance.flags.useSetAPIUrl && piwikTracker.setAPIUrl,
				matomoInstance.flags.useEnableCrossDomainLinking && piwikTracker.enableCrossDomainLinking,
				matomoInstance.flags.useSetCookiePath && piwikTracker.setCookiePath,
				matomoInstance.flags.useSetCookieDomain && piwikTracker.setCookieDomain,
				matomoInstance.flags.useSetDomains && piwikTracker.setDomains,
				matomoInstance.flags.useSetUserId && piwikTracker.setUserId,
				matomoInstance.flags.useEnableLinkTracking && piwikTracker.enableLinkTracking,
			];
			fixedFnOrder.forEach((fn) => fn && fn());

			matomoInstance.attachPiwikInstance(piwikTracker);
		}
	}

	function removeScript(): void {
		if (scriptElement == null) return;

		scriptElement.remove();

		window.piwikAsyncInit = undefined;
		window.Matomo = undefined;
		window.Piwik = undefined;
		window.piwik_log = undefined;
		window.AnalyticsTracker = undefined;
		window.piwikAsyncInit = undefined;
		window.matomoAsyncInit = undefined;
		window.matomoPluginAsyncInit = undefined;

		scriptElement = null;
	}

	function loadScript(urlBase: string): void {
		removeScript();

		// Is called when piwik script has been loaded
		window.piwikAsyncInit = () => {
			attachTrackers(urlBase);
			window.piwikAsyncInit = undefined;
		};

		const normalizedUrlBase = urlBase[urlBase.length - 1] !== '/' ? `${urlBase}/` : urlBase;

		scriptElement = document.createElement('script');
		scriptElement.type = 'text/javascript';
		scriptElement.async = true;
		scriptElement.defer = true;
		scriptElement.src = `${normalizedUrlBase}matomo.js`;

		document.head.insertBefore(scriptElement, null);
	}
}
